/**
 * Belora storefront enhancements: mood filter pills for a product grid, a
 * scroll-snap slider with previous/next buttons, and the floating collections menu
 * panel. Dependency-free custom elements.
 */

/**
 * Mood filter for the product list in the next section. "All" shows that list; every
 * other pill shows a pre-rendered group of products from its collection instead.
 */
class BeloraFilter extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {Element | null} The section whose product list is swapped out. */
  #target = null;

  /** Set before the element moves itself, because moving re-runs the connect callbacks. */
  #placed = false;

  connectedCallback() {
    // Resolve the target once: after the filter moves into it, the DOM position no longer points at it
    this.#target ??= this.closest('.shopify-section')?.nextElementSibling ?? null;
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.addEventListener('click', this.#onClick, { signal: this.#controller.signal });
    if (!this.#placed) this.#place();
    this.hidden = false;
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /** @returns {HTMLElement[]} The target section's own product lists (grid and mobile carousel). */
  get #lists() {
    const target = this.#target;
    if (!target) return [];

    /** @type {HTMLElement[]} */
    const lists = [];
    for (const list of target.querySelectorAll('.resource-list')) {
      if (list instanceof HTMLElement && !this.contains(list)) lists.push(list);
    }
    return lists;
  }

  /** Moves the filter directly above the product list of the target section. */
  #place() {
    if (this.#placed) return;

    const anchor = this.#lists[0];
    if (!anchor?.parentElement) return;

    this.#placed = true;
    this.classList.add('belora-filter--placed');
    anchor.parentElement.insertBefore(this, anchor);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const pill = event.target.closest('button[data-group]');
    if (!(pill instanceof HTMLButtonElement)) return;

    const selected = pill.dataset.group ?? '*';
    const showAll = selected === '*';

    for (const button of this.querySelectorAll('button[data-group]')) {
      button.setAttribute('aria-pressed', String(button === pill));
    }

    let count = 0;
    for (const group of this.querySelectorAll('.belora-filter__group')) {
      if (!(group instanceof HTMLElement)) continue;
      const active = !showAll && group.dataset.group === selected;
      group.hidden = !active;
      if (active) count = Number(group.dataset.count ?? 0);
    }

    for (const list of this.#lists) {
      list.style.display = showAll ? '' : 'none';
      if (showAll) count = Math.max(count, list.querySelectorAll('.resource-list__item').length);
    }

    const status = this.querySelector('[data-status]');
    if (status instanceof HTMLElement) {
      status.textContent = (status.dataset.template ?? '').replace('[count]', String(count));
    }
  };
}

/**
 * Horizontal scroll-snap list with previous/next buttons. The buttons are hidden
 * when everything fits and disabled at either end.
 */
class BeloraSlider extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {ResizeObserver | undefined} */
  #observer;

  connectedCallback() {
    const track = this.#track;
    if (!track) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.addEventListener('click', this.#onClick, { signal });
    track.addEventListener('scroll', this.#update, { signal, passive: true });
    this.#observer = new ResizeObserver(this.#update);
    this.#observer.observe(track);
    this.#update();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
  }

  /** @returns {HTMLElement | null} */
  get #track() {
    const track = this.querySelector('[data-track]');
    return track instanceof HTMLElement ? track : null;
  }

  /** @param {Event} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('button[data-direction]');
    const track = this.#track;
    if (!(button instanceof HTMLButtonElement) || !track) return;

    const card = track.firstElementChild;
    const step = card instanceof HTMLElement ? card.offsetWidth + parseFloat(getComputedStyle(track).columnGap || '0') : track.clientWidth;
    const direction = button.dataset.direction === 'previous' ? -1 : 1;
    const rtl = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    track.scrollBy({ left: step * direction * rtl, behavior: reduced ? 'auto' : 'smooth' });
  };

  #update = () => {
    const track = this.#track;
    if (!track) return;

    const max = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);
    const controls = this.querySelector('[data-controls]');
    if (controls instanceof HTMLElement) controls.hidden = max <= 1;

    for (const button of this.querySelectorAll('button[data-direction]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      button.disabled = button.dataset.direction === 'previous' ? position <= 1 : position >= max - 1;
    }
  };
}

/**
 * Floating collections panel in the header menu. Lines the panel up under its own
 * menu item and keeps it inside the viewport; without this script it stays centred.
 */
class BeloraMegaMenu extends HTMLElement {
  /** @type {AbortController | undefined} */
  #controller;

  /** @type {ResizeObserver | undefined} */
  #observer;

  /** @type {number | undefined} */
  #frame;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const item = this.#item;

    item?.addEventListener('pointerenter', this.#schedule, { signal });
    item?.addEventListener('focusin', this.#schedule, { signal });
    this.#observer?.disconnect();
    this.#observer = new ResizeObserver(this.#schedule);
    this.#observer.observe(document.documentElement);
    if (item) this.#observer.observe(item);
    this.#schedule();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  /** @returns {HTMLElement | null} The top-level menu item that owns this panel. */
  get #item() {
    const item = this.closest('.menu-list__list-item');
    return item instanceof HTMLElement ? item : null;
  }

  #schedule = () => {
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(this.#place);
  };

  #place = () => {
    const item = this.#item;
    const edge = 16;

    // Inside the More panel, or in a right-to-left layout, keep the stylesheet's own placement
    if (!item || item.slot !== '' || getComputedStyle(this).direction === 'rtl') {
      this.style.removeProperty('--belora-mega-offset');
      return;
    }

    const anchor = item.getBoundingClientRect();
    if (anchor.width === 0) return;

    // Measure from the unshifted position so repeated runs give the same result
    this.style.setProperty('--belora-mega-offset', '0px');
    const panel = this.getBoundingClientRect();
    const viewport = document.documentElement.clientWidth;
    const wanted = anchor.left - 12;
    const left = Math.max(edge, Math.min(wanted, viewport - panel.width - edge));

    this.style.setProperty('--belora-mega-offset', Math.round(left - panel.left) + 'px');
  };
}

if (!customElements.get('belora-filter')) customElements.define('belora-filter', BeloraFilter);
if (!customElements.get('belora-slider')) customElements.define('belora-slider', BeloraSlider);
if (!customElements.get('belora-mega-menu')) customElements.define('belora-mega-menu', BeloraMegaMenu);
